import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { apiClient } from '../api/client';
import type { PatientMedicalOrganization } from '../constants/patient';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';

type OrgKind = 'referral' | 'family_doctor';

const TAB_KINDS: OrgKind[] = ['referral', 'family_doctor'];
const TAB_LABELS: Record<OrgKind, string> = {
  referral: '紹介元',
  family_doctor: 'かかりつけ医',
};

type OrgForm = {
  name: string;
  address: string;
  default_phone: string;
};

const emptyForm = (): OrgForm => ({ name: '', address: '', default_phone: '' });

const SettingsPage: React.FC = () => {
  const { showSnackbar } = useAppSnackbar();
  const [tab, setTab] = useState(0);
  const kind = TAB_KINDS[tab];

  const [rows, setRows] = useState<PatientMedicalOrganization[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PatientMedicalOrganization | null>(null);
  const [form, setForm] = useState<OrgForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<PatientMedicalOrganization | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<PatientMedicalOrganization[]>('/api/patient-organizations', {
        params: {
          kind,
          listAll: '1',
          q: search.trim() || undefined,
        },
      });
      setRows(res.data);
    } catch {
      setError('一覧の取得に失敗しました');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [kind, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (org: PatientMedicalOrganization) => {
    setEditing(org);
    setForm({
      name: org.name,
      address: org.address ?? '',
      default_phone: org.default_phone ?? '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      setFormError('名称は必須です');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name,
        address: form.address.trim() || null,
        default_phone: form.default_phone.trim() || null,
      };
      if (editing) {
        await apiClient.patch(`/api/patient-organizations/${editing.id}`, payload);
        showSnackbar({ message: `${TAB_LABELS[kind]}を更新しました`, severity: 'success' });
      } else {
        await apiClient.post('/api/patient-organizations', { kind, ...payload });
        showSnackbar({ message: `${TAB_LABELS[kind]}を追加しました`, severity: 'success' });
      }
      setDialogOpen(false);
      await load();
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setFormError(String(e.response.data.error));
      } else {
        setFormError(editing ? '更新に失敗しました' : '追加に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  const closeDeleteConfirm = () => {
    if (!deleting) setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/api/patient-organizations/${deleteTarget.id}`);
      showSnackbar({ message: `${TAB_LABELS[kind]}を削除しました`, severity: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || '削除に失敗しました', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        設定
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v: number) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {TAB_KINDS.map((k) => (
          <Tab key={k} label={TAB_LABELS[k]} />
        ))}
      </Tabs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="名称で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {TAB_LABELS[kind]}を追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell align="right" width={104}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>読み込み中…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  {search.trim()
                    ? '該当する項目がありません'
                    : `${TAB_LABELS[kind]}がまだ登録されていません`}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {row.address?.trim() || '—'}
                  </TableCell>
                  <TableCell>{row.default_phone?.trim() || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="編集" size="small" onClick={() => openEdit(row)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label="削除"
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(row)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? `${TAB_LABELS[kind]}を編集` : `${TAB_LABELS[kind]}を追加`}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            required
            fullWidth
            label="名称"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="住所"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="電話番号"
            value={form.default_phone}
            onChange={(e) => setForm((f) => ({ ...f, default_phone: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget !== null} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{TAB_LABELS[kind]}を削除しますか？</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              「{deleteTarget.name}」を削除します。この操作は取り消せません。
              患者情報でこの{TAB_LABELS[kind]}が設定されている場合は、設定が解除されます。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDelete()}
            disabled={deleting}
          >
            {deleting ? '削除中…' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
